import { FC, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AvailablePaymentCurrency } from "@simplyk/common";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { PrizeCard } from "../../components/PrizeCard/PrizeCard";
import { PrizeThumbnail } from "../../components/PrizeThumbnail/PrizeThumbnail";
import { TicketingPrizeOutput } from "../../types";

const SLIDE_GAP = 4;

// Swiper's base stylesheet as Emotion styles: a global CSS import never reaches the live editor preview iframe.
const SWIPER_BASE_SX = {
  "& :where(.swiper)": {
    position: "relative",
    display: "block",
    overflow: "hidden",
    marginInline: "auto",
    padding: 0,
    listStyle: "none",
    zIndex: 1,
  },
  "& :where(.swiper-wrapper)": {
    position: "relative",
    display: "flex",
    width: "100%",
    height: "100%",
    zIndex: 1,
    boxSizing: "content-box",
    transitionProperty: "transform",
  },
  "& :where(.swiper-horizontal)": { touchAction: "pan-y" },
  "& :where(.swiper-slide)": {
    position: "relative",
    display: "block",
    flexShrink: 0,
    width: "100%",
    height: "100%",
    transitionProperty: "transform",
  },
} as const;

interface MobilePrizeCarouselProps {
  prizes: TicketingPrizeOutput[];
  currency: AvailablePaymentCurrency;
  selectedIndex: number;
  onSelect: (index: number) => void;
  onOpen: (prize: TicketingPrizeOutput) => void;
}

export const MobilePrizeCarousel: FC<MobilePrizeCarouselProps> = ({
  prizes,
  currency,
  selectedIndex,
  onSelect,
  onOpen,
}) => {
  const [cardsSwiper, setCardsSwiper] = useState<SwiperInstance | null>(null);
  const [thumbnailsSwiper, setThumbnailsSwiper] = useState<SwiperInstance | null>(null);

  useEffect(() => {
    if (cardsSwiper && cardsSwiper.activeIndex !== selectedIndex) {
      cardsSwiper.slideTo(selectedIndex);
    }
    thumbnailsSwiper?.slideTo(selectedIndex);
  }, [cardsSwiper, thumbnailsSwiper, selectedIndex]);

  return (
    <Stack sx={{ marginInline: -2, ...SWIPER_BASE_SX }}>
      <Box
        sx={(theme) => ({
          "& .swiper-slide": { width: `calc(100% - ${theme.spacing(4)})`, height: "auto", display: "flex" },
        })}
      >
        <Swiper
          onSwiper={setCardsSwiper}
          onSlideChange={(swiper) => onSelect(swiper.activeIndex)}
          initialSlide={selectedIndex}
          slidesPerView="auto"
          centeredSlides
          spaceBetween={SLIDE_GAP}
        >
          {prizes.map((prize, index) => (
            <SwiperSlide key={prize.id}>
              <PrizeCard
                prize={prize}
                currency={currency}
                variant="mobile"
                isDimmed={index !== selectedIndex}
                onClick={() => (index === selectedIndex ? onOpen(prize) : onSelect(index))}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
      {prizes.length > 1 && (
        <Box
          sx={{
            paddingBlock: 0.5,
            // Padding instead of slidesOffsetBefore/After: Swiper's centeredSlidesBounds shifts offset rows left when they fit.
            "& .swiper": { paddingInline: 2 },
            "& .swiper-slide": { width: "auto" },
          }}
        >
          <Swiper
            onSwiper={setThumbnailsSwiper}
            initialSlide={selectedIndex}
            slidesPerView="auto"
            centeredSlides
            centeredSlidesBounds
            spaceBetween={SLIDE_GAP}
          >
            {prizes.map((prize, index) => (
              <SwiperSlide key={prize.id}>
                <PrizeThumbnail
                  prize={prize}
                  index={index}
                  isSelected={index === selectedIndex}
                  onClick={() => onSelect(index)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      )}
    </Stack>
  );
};
